import React, { useEffect, useState } from "react";
import {
  TextField,
  Box,
  FormControl,
  Grid,
  Button,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Input,
  FormHelperText,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  Tabs,
  Divider,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon
} from "@mui/icons-material";
import { ExecutePostWithParams, GetDataFromApiWithParams } from "../../../../../services/api/ExecuteApiRequests";
import { IPDCToastMessageResult } from "../../../../../components/Controls";
import useDecodedUser from "../../../../../services/hooks/useDecodedUser";

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`collect-payment-tabpanel-${index}`}
      aria-labelledby={`collect-payment-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const initialData = {
  invoice_no: "",
  agreement_id: "",
  amount: "",
  payment_date: "",
  payment_type: "Shed Rental",
  bank_name: "",
  reference_no: "",
  description: "",
  if_late_penality_interest_amount: "0.0",
  recieved_by: "",
  payment_period: "",
  arrear_skipped_amount: "",
  arrear_skipping_description: "",
  attachment: null
};

const steps = [
  {
    title: "Basic Information",
    fields: ["invoice_no", "agreement_id", "amount", "payment_date"],
    requiredFields: ["invoice_no", "agreement_id", "amount", "payment_date"],
    disabledFields: ["agreement_id", "amount","invoice_no"]
  },
  {
    title: "Payment Details",
    fields: ["bank_name", "reference_no", "description", "payment_type"],
    requiredFields: ["bank_name", "reference_no", "payment_type"],
    disabledFields: ["payment_type"],
  },
  { 
    title: 'Arrears Information', 
    fields: ['arrear_skipped_amount', 'arrear_skipping_description', 'attachment'],
    requiredFields: [],
    disabledFields: ["arrear_skipped_amount", "arrear_skipping_description"],
  },
  {
    title: "Additional Info",
    fields: ["if_late_penality_interest_amount", "recieved_by", "payment_period"],
    requiredFields: ["recieved_by"],
    disabledFields: ["recieved_by"],
  },
];

const CollectPayment = () => {
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Original form states
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState("");
  const [selectedEntireInvoice, setSelectedEntireInvoice] = useState({});
  const [paymentType, setPaymentType] = useState(initialData.payment_type);
  const [formData, setFormData] = useState(initialData);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [agreementDetail, setAgreementDetail] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  const [isPaymentLessThanRent, setIsPaymentLessThanRent] = useState(false);
  const [invoiceNos, setInvoiceNos] = useState([]);
  const [invoiceLists, setInvoiceLists] = useState([]);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
  const decodedUser = useDecodedUser();

  // Tab 2 states - Payment List
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dateFilter, setDateFilter] = useState({
    fromDate: "",
    toDate: ""
  });
  const [typeFilter, setTypeFilter] = useState("all");

  // Debug effect to log message changes
  useEffect(() => {
    if (successMessage) {
      console.log("✅ Success message set:", successMessage);
    }
    if (errorMessage) {
      console.log("❌ Error message set:", errorMessage);
    }
  }, [successMessage, errorMessage]);

  useEffect(() => {
    if (selectedAgreement) {
      setFormData(prevData => ({ ...prevData, agreement_id: selectedAgreement.id }));
    }
    if (agreementDetail && agreementDetail.length > 0) {
      const monthlyRent = agreementDetail[0].monthly_rent;
      setIsPaymentLessThanRent(parseFloat(formData.amount) < parseFloat(monthlyRent));
    }
  }, [selectedAgreement, agreementDetail, formData.amount]);

  // Update form fields when selectedInvoice changes
  useEffect(() => {
    if (selectedInvoice) {
      // Find the selected invoice in invoiceLists
      const selectedInvoiceData = invoiceLists.find(
        invoice => (invoice.invoice_num || invoice.invoiceNumber || invoice.invoice) === selectedInvoice
      );
      
      if (selectedInvoiceData) {
        setSelectedEntireInvoice(selectedInvoiceData);
        
        // Determine payment type based on ownership
        const ownershipType = selectedInvoiceData.ownership_type || 
                             (selectedInvoiceData.rent_payment ? "rent" : "lease");
        const paymentTypeValue = ownershipType === "Shed Rental" ? "Shed Rental" : "Developed Land Lease";
        
        // Update form data with invoice information
        setFormData(prevData => ({
          ...prevData,
          invoice_no: selectedInvoice,
          agreement_id: selectedInvoiceData.agreement_id || prevData.agreement_id,
          amount: selectedInvoiceData.total_amount || prevData.amount,
          payment_type: paymentTypeValue,
          arrear_skipped_amount: selectedInvoiceData.discount !== undefined && selectedInvoiceData.discount !== null 
            ? String(selectedInvoiceData.discount) 
            : "0.0",
          arrear_skipping_description: selectedInvoiceData.note || "",
        }));
        
        // Also update the paymentType state
        setPaymentType(paymentTypeValue);
      }
    }
  }, [selectedInvoice, invoiceLists]);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const resultData2 = await GetDataFromApiWithParams(`finances/allInvoices`);
        setInvoiceLists(resultData2);
  
        if (resultData2 && resultData2.length > 0) {
          // Extract invoice numbers based on your data structure
          const extractedInvoiceNos = resultData2.map(item => item.invoice_num || item.invoiceNumber || item.invoice);
          setInvoiceNos(extractedInvoiceNos);
        }
      } catch (error) {
        console.error("Failed to fetch invoices:", error);
      }
    };
    
    fetchInvoices();
    
    // Fix: Check if decodedUser exists before accessing full_name
    if (decodedUser && decodedUser.full_name) {
      setFormData(prevData => ({
        ...prevData,
        recieved_by: decodedUser.full_name
      }));
    }
  }, [decodedUser]); // Add decodedUser as dependency

  const fetchAgreementDetails = async () => {
    try {
      const agreementId = formData.agreement_id || (selectedAgreement && selectedAgreement.id) || selectedEntireInvoice.agreement_id;
      console.log(formData.agreement_id, formData.ownership_type)
      if (!agreementId) return;
  
      const resultData = await GetDataFromApiWithParams(`agreements/${agreementId}`);
      
      if (resultData && resultData.length > 0) {
        setAgreementDetail(resultData);
        
        // Update payment type based on agreement ownership
        const ownershipType = resultData[0].ownership_type;
        
        // Map ownership types to payment type values
        let paymentTypeValue = "Shed Rent"; // Default
        console.log("AAAAAAAAAAAAAAAA",ownershipType)
        if (ownershipType) {
          const typeLower = ownershipType.toString().toLowerCase().trim();
          
          if (typeLower.includes('shed') || typeLower === 'shed rent' || typeLower === 'shed rental') {
            paymentTypeValue = "Shed Rental";
          } else if (typeLower.includes('land') || typeLower.includes('lease') || 
                    typeLower === 'developed land lease' || typeLower === 'land leased') {
            paymentTypeValue = "Land Lease";
          } else if (typeLower.includes('residential') || typeLower === 'residential') {
            paymentTypeValue = "Residential";
          } else if (typeLower.includes('commercial') || typeLower === 'commercial') {
            paymentTypeValue = "Commercial";
          }
        }
        
        setPaymentType(paymentTypeValue);
        setFormData(prevData => ({
          ...prevData,
          payment_type: paymentTypeValue
        }));
        
        console.log(`Agreement ownership type: ${ownershipType} mapped to payment type: ${paymentTypeValue}`);
      }
    } catch (error) {
      console.error("Failed to fetch agreement details:", error);
    }
  };

  // Tab 2: Fetch payments
  const fetchPayments = async () => {
    try {
      setIsLoadingPayments(true);
      
      const params = {
        from_date: dateFilter.fromDate || null,
        to_date: dateFilter.toDate || null,
        payment_type: typeFilter !== "all" ? typeFilter : null
      };
      
      console.log("Fetching payments with params:", params);
      const result = await GetDataFromApiWithParams("finances/periodpaymentsearch", params);
      
      console.log("Payments response:", result);
      
      if (result && Array.isArray(result)) {
        setPayments(result);
        setFilteredPayments(result);
        setTotalRecords(result.length);
        setPage(0);
      } else {
        setPayments([]);
        setFilteredPayments([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
      setPayments([]);
      setFilteredPayments([]);
      setTotalRecords(0);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const fetchPayments2 = async () => {
    try {
      setIsLoadingPayments(true);
      
      const params = {
        from_date: dateFilter.fromDate || null,
        to_date: dateFilter.toDate || null,
        payment_type: typeFilter !== "all" ? typeFilter : null
      };
      
      console.log("Fetching payments with params:", params);
      const result = await GetDataFromApiWithParams("finances/periodpaymentsearch2", params);
      
      console.log("Payments response:", result);
      
      if (result && Array.isArray(result)) {
        setPayments(result);
        setFilteredPayments(result);
        setTotalRecords(result.length);
        setPage(0);
      } else {
        setPayments([]);
        setFilteredPayments([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
      setPayments([]);
      setFilteredPayments([]);
      setTotalRecords(0);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  // Clear filters for tab 2
  const clearFilters = () => {
    setDateFilter({
      fromDate: "",
      toDate: ""
    });
    setTypeFilter("all");
    setPayments([]);
    setFilteredPayments([]);
    setTotalRecords(0);
  };

  // Print functionality for Tab 2
 // Print functionality for Tab 2
const handlePrint = () => {
  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow pop-ups to print');
    return;
  }

  // Get current date for report header
  const currentDate = new Date().toLocaleString();
  
  // Build filter info text
  let filterInfo = 'All Payments';
  if (dateFilter.fromDate || dateFilter.toDate) {
    filterInfo = `Date Range: ${dateFilter.fromDate || 'Start'} to ${dateFilter.toDate || 'End'}`;
  }
  if (typeFilter !== 'all') {
    filterInfo += filterInfo ? ' | ' : '';
    filterInfo += `Payment Type: ${typeFilter}`;
  }

  // Calculate summaries (exactly as in UI)
  const usdPayments = filteredPayments.filter(p => p.payment_mode?.toUpperCase() === 'USD' || p.payment_mode?.toUpperCase() === '$');
  const birrPayments = filteredPayments.filter(p => !p.payment_mode || p.payment_mode?.toUpperCase() === 'BIRR' || p.payment_mode?.toUpperCase() === 'ETB' || p.payment_mode?.toUpperCase() !== 'USD');
  
  const usdTotal = usdPayments.reduce((sum, p) => sum + (parseFloat(String(p.amount).replace(/[^0-9.-]/g, '')) || 0), 0).toFixed(2);
  const birrTotal = birrPayments.reduce((sum, p) => sum + (parseFloat(String(p.amount).replace(/[^0-9.-]/g, '')) || 0), 0).toFixed(2);

  const shedRentalCount = filteredPayments.filter(p => p.payment_type?.toLowerCase() === 'shed rental').length;
  const landLeasedCount = filteredPayments.filter(p => p.payment_type?.toLowerCase() === 'land leased').length;
  const residentialCount = filteredPayments.filter(p => p.payment_type?.toLowerCase() === 'residential').length;
  const commercialCount = filteredPayments.filter(p => p.payment_type?.toLowerCase() === 'commercial').length;

  // Create print content with exact UI styling
  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Payment Collection Report</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #333;
            background-color: white;
          }
          .print-container {
            max-width: 1200px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #1976d2;
            padding-bottom: 15px;
          }
          .header h1 {
            margin: 0;
            color: #1976d2;
            font-size: 24px;
          }
          .header p {
            margin: 5px 0 0;
            color: #666;
            font-size: 12px;
          }
          .filter-info {
            background-color: #f5f5f5;
            padding: 10px 15px;
            margin-bottom: 20px;
            border-radius: 4px;
            font-size: 13px;
            border: 1px solid #e0e0e0;
          }
          .filter-info strong {
            color: #1976d2;
          }
          
          /* Summary Section - Exactly matching UI */
          .summary-section {
            margin-bottom: 30px;
          }
          .summary-paper {
            background-color: #f0f7ff;
            padding: 16px;
            border-radius: 4px;
            border: 1px solid #b0c4de;
            width: 350px;
            float: right;
            margin-bottom: 20px;
          }
          .summary-title {
            font-weight: bold;
            color: #1976d2;
            margin-bottom: 16px;
            border-bottom: 1px solid #b0c4de;
            padding-bottom: 8px;
            font-size: 14px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
          }
          .summary-label {
            color: #666;
            font-size: 13px;
          }
          .summary-value {
            font-weight: bold;
            font-size: 13px;
          }
          .summary-value.currency {
            color: #1976d2;
          }
          .summary-subtitle {
            font-weight: bold;
            color: #1976d2;
            display: block;
            margin-bottom: 8px;
            margin-top: 8px;
            font-size: 12px;
          }
          .summary-subtitle:first-of-type {
            margin-top: 0;
          }
          .divider {
            border-top: 1px solid #b0c4de;
            margin: 12px 0;
          }
          .note {
            display: block;
            margin-top: 16px;
            color: #CC0000;
            font-style: italic;
            font-size: 11px;
          }
          
          /* Clear float */
          .clearfix::after {
            content: "";
            clear: both;
            display: table;
          }
          
          /* Table Styles */
          .table-container {
            margin-top: 20px;
            overflow-x: auto;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }
          th {
            background-color: #1976d2;
            color: white;
            padding: 10px 8px;
            text-align: left;
            border: 1px solid #ddd;
            font-weight: bold;
          }
          td {
            padding: 8px;
            text-align: left;
            border: 1px solid #ddd;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .amount-cell {
            font-weight: bold;
            color: #1976d2;
          }
          
          /* Footer */
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #999;
            border-top: 1px solid #ddd;
            padding-top: 15px;
          }
          
          @media print {
            body {
              margin: 0;
              padding: 10px;
            }
            .no-print {
              display: none;
            }
            .summary-paper {
              background-color: #f0f7ff !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            th {
              background-color: #1976d2 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <div class="header">
            <h1>Payment Collection Report</h1>
            <p>Generated on: ${currentDate}</p>
          </div>
          
          <div class="filter-info">
            <strong>Filters Applied:</strong> ${filterInfo} | <strong>Total Records:</strong> ${totalRecords}
          </div>
          
          <!-- Summary Section - Exactly matching UI -->
          <div class="clearfix">
            <div class="summary-paper">
              <div class="summary-title">Summary</div>
              
              <!-- Total Records -->
              <div class="summary-row">
                <span class="summary-label">Total Records:</span>
                <span class="summary-value">${totalRecords}</span>
              </div>
              
              <!-- USD Section -->
              <div class="summary-subtitle">USD Payments</div>
              <div class="summary-row" style="margin-left: 8px;">
                <span class="summary-label">Count:</span>
                <span class="summary-value">${usdPayments.length}</span>
              </div>
              <div class="summary-row" style="margin-left: 8px;">
                <span class="summary-label">Total Amount:</span>
                <span class="summary-value currency">$${usdTotal}</span>
              </div>
              
              <!-- Birr Section -->
              <div class="summary-subtitle">Birr Payments</div>
              <div class="summary-row" style="margin-left: 8px;">
                <span class="summary-label">Count:</span>
                <span class="summary-value">${birrPayments.length}</span>
              </div>
              <div class="summary-row" style="margin-left: 8px;">
                <span class="summary-label">Total Amount:</span>
                <span class="summary-value currency">${birrTotal} ETB</span>
              </div>
              
              <div class="divider"></div>
              
              <!-- Payment Type Breakdown -->
              <div class="summary-subtitle" style="color: #666;">Payment Type Breakdown</div>
              <div class="summary-row" style="margin-left: 8px;">
                <span class="summary-label">Shed Rental:</span>
                <span class="summary-value currency">${shedRentalCount}</span>
              </div>
              <div class="summary-row" style="margin-left: 8px;">
                <span class="summary-label">Land Leased:</span>
                <span class="summary-value currency">${landLeasedCount}</span>
              </div>
              <div class="summary-row" style="margin-left: 8px;">
                <span class="summary-label">Residential:</span>
                <span class="summary-value currency">${residentialCount}</span>
              </div>
              <div class="summary-row" style="margin-left: 8px;">
                <span class="summary-label">Commercial:</span>
                <span class="summary-value currency">${commercialCount}</span>
              </div>
              
              <!-- Note -->
              <span class="note">Note: USD and Birr amounts are shown separately. Exchange rate not applied.</span>
            </div>
          </div>
          
          <!-- Results Table -->
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Invoice No</th>
                  <th>Agreement ID</th>
                  <th>Payment Date</th>
                  <th>Amount</th>
                  <th>Payment Type</th>
                  <th>Bank Name</th>
                  <th>Reference No</th>
                  <th>Received By</th>
                </tr>
              </thead>
              <tbody>
                ${filteredPayments.map((payment, index) => {
                  const cleanAmount = String(payment.amount).replace(/[^0-9.-]/g, '');
                  const numericAmount = parseFloat(cleanAmount);
                  const formattedAmount = !isNaN(numericAmount) ? numericAmount.toFixed(2) : '0.00';
                  let amountDisplay = '';
                  let amountClass = 'amount-cell';
                  
                  if (payment.payment_mode === 'USD' || payment.payment_mode === 'usd' || payment.payment_mode === '$') {
                    amountDisplay = `$${formattedAmount}`;
                  } else {
                    amountDisplay = `${formattedAmount} ETB`;
                  }
                  
                  return `
                    <tr>
                      <td>${index + 1}</td>
                      <td>${payment.invoice_no || '-'}</td>
                      <td>${payment.agreement_id || '-'}</td>
                      <td>${formatDateForPrint(payment.payment_date)}</td>
                      <td class="${amountClass}">${amountDisplay}</td>
                      <td>${payment.payment_type || '-'}</td>
                      <td>${payment.bank_name || '-'}</td>
                      <td>${payment.reference_no || '-'}</td>
                      <td>${payment.recieved_by || payment.received_by || '-'}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
          
          <div class="footer">
            <p>This is a system-generated report. For any discrepancies, please contact the finance department.</p>
            <p>Printed on: ${currentDate}</p>
          </div>
        </div>
      </body>
    </html>
  `;

  // Write to the new window and print
  printWindow.document.write(printContent);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  printWindow.onafterprint = function() {
    printWindow.close();
  };
};

// Helper function to format date for print

  // Helper function to format date for print
  const formatDateForPrint = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setFormData(prevData => ({ ...prevData, attachment: file }));
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
    if(name === 'amount' && agreementDetail && agreementDetail.length > 0) {
      const monthlyRent = agreementDetail[0].monthly_rent;
      setIsPaymentLessThanRent(parseFloat(value) < parseFloat(monthlyRent));
    }
  };

  const validateStep = stepIndex => {
    const errors = {};
    const step = steps[stepIndex];
    step.requiredFields.forEach(field => {
      if (!formData[field] || (field === "amount" && parseFloat(formData[field]) <= 0)) {
        errors[field] = "This field is required";
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    }
  };

  const handleBack = () => setActiveStep(prevActiveStep => prevActiveStep - 1);

  const handleSubmit = async e => {
    e.preventDefault();
    console.log("Form submitted");
    
    if (validateStep(steps.length - 1)) {
      console.log("Validation passed, submitting form data:", formData);
      
      try {
        const formDataForApi = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          if (key === 'attachment') {
            if (value) {
              formDataForApi.append('file', value);
            }
          } else {
            formDataForApi.append(key, value || '');
          }
        });
        
        const response = await ExecutePostWithParams("finances/collections", formDataForApi, true);
        console.log("API Response received:", response);
        
        setSuccessMessage("✅ Payment collection information successfully saved!");
        
        // Reset form
        setFormData(initialData);
        setSelectedInvoice("");
        setSelectedEntireInvoice({});
        setActiveStep(0);
        
        // Refresh payments list in tab 2
        fetchPayments();
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
        
      } catch (err) {
        console.error("❌ Error saving payment:", err);
        setErrorMessage(err.message || "Saving payment was unsuccessful, please try again!");
        
        setTimeout(() => {
          setErrorMessage(null);
        }, 5000);
      }
    } else {
      console.log("Validation failed");
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1 && payments.length === 0) {
      fetchPayments();
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toFixed(2);
  };

  // MODIFIED: renderTextField now uses disabledFields from step configuration
  const renderTextField = (name, label, type = "text") => {
    // Check if field should be disabled based on current step's disabledFields
    const isDisabled = steps[activeStep].disabledFields?.includes(name) || false;
    
    return (
      <TextField
        fullWidth
        label={label}
        name={name}
        value={formData[name] || ''}
        onChange={handleInputChange}
        type={type}
        size="small"
        required={steps[activeStep].requiredFields.includes(name)}
        error={!!validationErrors[name] || (name === 'amount' && isPaymentLessThanRent)}
        helperText={validationErrors[name] || (name === 'amount' && isPaymentLessThanRent ? "Payment amount is less than monthly rent." : '')}
        InputLabelProps={type === "date" ? { shrink: true } : {}}
        disabled={isDisabled} // Use disabled instead of readOnly
        sx={isDisabled ? { 
          '& .MuiInputBase-input.Mui-disabled': {
            backgroundColor: '#f5f5f5',
            cursor: 'not-allowed',
            color: 'rgba(0, 0, 0, 0.6)'
          }
        } : {}}
      />
    );
  };

  const renderStepContent = step => (
    <Grid container spacing={2}>
      {step.fields.map(field => {
        // Special handling for payment_type to show as text instead of radio
        if (field === "payment_type") {
          return (
            <Grid item xs={12} key={field}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Payment Type:
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  {formData.payment_type || "Not specified"}
                </Typography>
              </Box>
            </Grid>
          );
        }
        
        // Regular field handling
        return (
          <Grid item xs={12} key={field}>
            {field === 'attachment' ? (
              <FormControl fullWidth size="small">
                <Input
                  type="file"
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <FormHelperText>{formData.attachment ? formData.attachment.name : 'No file chosen'}</FormHelperText>
              </FormControl>
            ) : (
              renderTextField(field, field.split(/(?=[A-Z])/).join(" "), 
                field === "payment_date" ? "date" : 
                field === "amount" || field === "if_late_penality_interest_amount" || field === "arrear_skipped_amount" ? "number" : "text")
            )}
          </Grid>
        );
      })}
    </Grid>
  );

  const renderAgreementDetails = () =>
    agreementDetail && (
      <Box sx={{ p: 1 }}>
        <Grid container spacing={1}>
          {["id","park_name", "company_name", "ownership_type","monthly_rent","annual_mngmnt_service_fee", "contract_status"].map(key => (
            <React.Fragment key={key}>
              <Grid item xs={5}>
                <Typography variant="body2" fontWeight="bold">
                  {key.split("_").join(" ")}:
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography variant="body2">{agreementDetail[0][key]}</Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Box>
    );

  // Filter invoice numbers based on search term
  const filteredInvoiceNos = invoiceNos?.filter(invoiceNo => 
    invoiceNo?.toLowerCase().includes(invoiceSearchTerm.toLowerCase())
  ) || [];

  // Display invoice details when an invoice is selected
  const renderSelectedInvoiceDetails = () => {
    if (!selectedEntireInvoice || Object.keys(selectedEntireInvoice).length === 0) return null;
    
    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Selected Invoice Details:
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">Company:</Typography>
            <Typography variant="body2">{selectedEntireInvoice.company_name || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">Park:</Typography>
            <Typography variant="body2">{selectedEntireInvoice.park_name || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">Total Amount:</Typography>
            <Typography variant="body2" fontWeight="bold">${selectedEntireInvoice.total_amount || '0'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">Due Date:</Typography>
            <Typography variant="body2">{selectedEntireInvoice.due_date ? new Date(selectedEntireInvoice.due_date).toLocaleDateString() : 'N/A'}</Typography>
          </Grid>
          {selectedEntireInvoice.discount && (
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">Discount:</Typography>
              <Typography variant="body2">${selectedEntireInvoice.discount}</Typography>
            </Grid>
          )}
          {selectedEntireInvoice.note && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">Note:</Typography>
              <Typography variant="body2">{selectedEntireInvoice.note}</Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  // Common font style
  const fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  return (
    <Box sx={{ 
      maxWidth: '1200px', 
      margin: '0 auto',
      fontFamily: fontFamily,
      mt: 5
    }}>
      <Tabs value={tabValue} onChange={handleTabChange} aria-label="collect payment tabs">
        <Tab 
          label="Collect Payment" 
          sx={{ 
            mr: 2,
            fontFamily: fontFamily,
            fontSize: '0.95rem',
            fontWeight: 500
          }} 
        />
        <Tab 
          label="Payment List" 
          sx={{ 
            fontFamily: fontFamily,
            fontSize: '0.95rem',
            fontWeight: 500
          }} 
        />
      </Tabs>
      
      <Paper elevation={1} sx={{ p: 3, borderRadius: 1 }}>
        {/* Tab 1: Collect Payment */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" sx={{ 
            fontFamily: fontFamily,
            fontSize: '1.2rem',
            mb: 3,
            pb: 1,
            borderBottom: '1px solid #e0e0e0'
          }}>
            Collect Payment
          </Typography>

          {/* Toast Message Display */}
          {successMessage && (
            <Box sx={{ mb: 2 }}>
              <IPDCToastMessageResult
                message={successMessage}
                type="success"
              />
            </Box>
          )}
          
          {errorMessage && (
            <Box sx={{ mb: 2 }}>
              <IPDCToastMessageResult
                message={errorMessage}
                type="error"
              />
            </Box>
          )}

          <Grid container spacing={3}>
            {/* RIGHT Column - Invoice Details */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f9f9f9', height: '100%' }}>
                <Typography variant="subtitle1" sx={{ 
                  fontFamily: fontFamily,
                  fontSize: '1.0rem',
                  mb: 2
                }}>
                  Invoice Detail
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={8}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <Select 
                            id="invoice-select"
                            value={selectedInvoice}
                            onChange={(e) => setSelectedInvoice(e.target.value)}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return <Typography color="textSecondary">Select an invoice number</Typography>;
                              }
                              return selected;
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 350,
                                  width: 350,
                                },
                              },
                            }}
                          >
                            {/* Search input inside menu */}
                            <MenuItem sx={{ pt: 1, pb: 1, cursor: 'default' }} disableRipple>
                              <TextField
                                size="small"
                                placeholder="Search invoice..."
                                value={invoiceSearchTerm}
                                onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                fullWidth
                                variant="standard"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{ my: 1 }}
                              />
                            </MenuItem>
                            
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            
                            {/* Map through filtered invoiceNos array */}
                            {filteredInvoiceNos.length > 0 ? (
                              filteredInvoiceNos.map((invoiceNo, index) => {
                                const invoiceData = invoiceLists.find(
                                  inv => (inv.invoice_num || inv.invoiceNumber || inv.invoice) === invoiceNo
                                );
                                
                                return (
                                  <MenuItem key={index} value={invoiceNo}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                      <Typography variant="body2">{invoiceNo}</Typography>
                                      {invoiceData && (
                                        <Typography variant="caption" color="textSecondary">
                                          {invoiceData.company_name} - ${invoiceData.total_amount}
                                          {invoiceData.discount ? ` (Discount: $${invoiceData.discount})` : ''}
                                        </Typography>
                                      )}
                                    </Box>
                                  </MenuItem>
                                );
                              })
                            ) : (
                              <MenuItem disabled>
                                <Typography color="textSecondary">No invoice numbers available</Typography>
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <Button 
                          startIcon={<SearchIcon />} 
                          onClick={fetchAgreementDetails}
                          size="small"
                          variant="outlined"
                          fullWidth
                          sx={{ 
                            textTransform: 'none',
                            fontFamily: fontFamily,
                            color: '#1976d2',
                            borderColor: '#1976d2',
                            '&:hover': {
                              borderColor: '#1565c0',
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            }
                          }}
                        >
                          View Detail
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                
                {/* Display selected invoice details */}
                {renderSelectedInvoiceDetails()}
                
                {renderAgreementDetails()}
              </Paper>
            </Grid>

            {/* LEFT Column - Stepper Form */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                <Stepper activeStep={activeStep} orientation="vertical">
                  {steps.map((step, index) => (
                    <Step key={step.title}>
                      <StepLabel>{step.title}</StepLabel>
                      <StepContent>
                        {renderStepContent(step)}
                        <Box sx={{ mb: 2, mt: 1 }}>
                          <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            size="small"
                            sx={{ 
                              mr: 1,
                              color: '#000080'
                            }}
                          >
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            color="inherit"
                            onClick={index === steps.length - 1 ? handleSubmit : handleNext}
                            size="small"
                          >
                            {index === steps.length - 1 ? 'Finish' : 'Next'}
                          </Button>
                        </Box>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 2: Payment List with Print Functionality */}
        <TabPanel value={tabValue} index={1}>
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
    <Typography variant="h6" sx={{ 
      fontFamily: fontFamily,
      fontSize: '1.0rem',
      pb: 1,
      borderBottom: '1px solid #e0e0e0'
    }}>
      Payment Collection List
    </Typography>
    
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<PrintIcon />}
        onClick={handlePrint}
        disabled={filteredPayments.length === 0 || isLoadingPayments}
        sx={{
          fontFamily: fontFamily,
          textTransform: 'none',
          borderColor: '#1976d2',
          color: '#1976d2',
          '&:hover': {
            borderColor: '#1565c0',
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          },
        }}
      >
        Print List
      </Button>
    </Box>
  </Box>

  {/* Filter Section */}
  <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f9f9f9' }}>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          type="date"
          name="fromDate"
          label="From Date"
          value={dateFilter.fromDate}
          onChange={(e) => setDateFilter(prev => ({ ...prev, fromDate: e.target.value }))}
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          type="date"
          name="toDate"
          label="To Date"
          value={dateFilter.toDate}
          onChange={(e) => setDateFilter(prev => ({ ...prev, toDate: e.target.value }))}
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Payment Type</InputLabel>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            label="Payment Type"
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="Shed Rental">Shed Rental</MenuItem>
            <MenuItem value="Land Leased">Land Leased</MenuItem>
            <MenuItem value="Residential">Residential</MenuItem>
            <MenuItem value="Commercial">Commercial</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={2}>
        <Button
          fullWidth
          type="submit"
          variant="contained"
          color="inherit"
          size="large"
          onClick={fetchPayments2}
          disabled={isLoadingPayments}
          startIcon={<SearchIcon />}
          sx={{ textTransform: 'none', height: '40px' }}
        >
          {isLoadingPayments ? 'Searching...' : 'Search'}
        </Button>
      </Grid>
      <Grid item xs={12} md={2}>
        <Button
          fullWidth
          variant="outlined"
          onClick={clearFilters}
          disabled={isLoadingPayments}
          sx={{ 
            textTransform: 'none', 
            height: '40px',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          Clear Filters
        </Button>
      </Grid>
    </Grid>
  </Paper>

  {/* Results Table */}
  <Paper sx={{ width: '100%', overflow: 'hidden' }}>
    <TableContainer sx={{ maxHeight: 500 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>S.No</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Invoice No</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Agreement ID</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Payment Date</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Payment Type</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Bank Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Reference No</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Received By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoadingPayments ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                <CircularProgress size={30} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Loading payments...
                </Typography>
              </TableCell>
            </TableRow>
          ) : filteredPayments.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                <ReceiptIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                <Typography variant="body1" color="textSecondary">
                  No payments found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Please adjust your filters and try again
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredPayments
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((payment, index) => {
                // Check if payment mode is USD/dollar
                const isUSDPayment = payment.payment_mode === 'USD' || 
                                     payment.payment_mode === 'usd' || 
                                     payment.payment_mode === '$';
                
                return (
                  <TableRow 
                    key={payment.id || index} 
                    hover
                    sx={{
                      backgroundColor: isUSDPayment ? '#e8f5e9' : 'inherit',
                      '&:hover': {
                        backgroundColor: isUSDPayment ? '#c8e6c9' : '#f5f5f5',
                      }
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{payment.invoice_no || '-'}</TableCell>
                    <TableCell>{payment.agreement_id || '-'}</TableCell>
                    <TableCell>{formatDate(payment.payment_date)}</TableCell>
                    <TableCell>
                      <Typography sx={{  color: 'black' }}>
                        {(() => {
                          const cleanAmount = String(payment.amount).replace(/[^0-9.-]/g, '');
                          const numericAmount = parseFloat(cleanAmount);
                          const formattedAmount = !isNaN(numericAmount) ? numericAmount.toFixed(2) : '0.00';
                          
                          if (isUSDPayment) {
                            return `$${formattedAmount}`;
                          } else {
                            return `${formattedAmount} ETB`;
                          }
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell>{payment.payment_type || '-'}</TableCell>
                    <TableCell>{payment.bank_name || '-'}</TableCell>
                    <TableCell>{payment.reference_no || '-'}</TableCell>
                    <TableCell>{payment.recieved_by || payment.received_by || '-'}</TableCell>
                  </TableRow>
                );
              })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    
    {/* Pagination */}
    {filteredPayments.length > 0 && (
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    )}
  </Paper>

  {/* Summary Section */}
  {filteredPayments.length > 0 && (
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Paper sx={{ p: 2, backgroundColor: '#f0f7ff', minWidth: '350px' }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: '#1976d2', borderBottom: '1px solid #b0c4de', pb: 1 }}>
          Summary
        </Typography>
        
        {/* Total Records */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Total Records:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{totalRecords}</Typography>
          </Grid>
        </Grid>
        
        {/* USD Section */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2', display: 'block', mb: 1 }}>
          USD Payments
        </Typography>
        <Grid container spacing={1} sx={{ mb: 1, pl: 1 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Count:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {filteredPayments.filter(p => p.payment_mode?.toUpperCase() === 'USD' || p.payment_mode?.toUpperCase() === '$').length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Total Amount:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              ${filteredPayments
                .filter(p => p.payment_mode?.toUpperCase() === 'USD' || p.payment_mode?.toUpperCase() === '$')
                .reduce((sum, p) => sum + (parseFloat(String(p.amount).replace(/[^0-9.-]/g, '')) || 0), 0)
                .toFixed(2)}
            </Typography>
          </Grid>
        </Grid>
        
        {/* Birr Section */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2', display: 'block', mb: 1, mt: 1 }}>
          Birr Payments
        </Typography>
        <Grid container spacing={1} sx={{ mb: 2, pl: 1 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Count:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {filteredPayments.filter(p => !p.payment_mode || p.payment_mode?.toUpperCase() === 'BIRR' || p.payment_mode?.toUpperCase() === 'ETB' || p.payment_mode?.toUpperCase() !== 'USD').length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Total Amount:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {filteredPayments
                .filter(p => !p.payment_mode || p.payment_mode?.toUpperCase() === 'BIRR' || p.payment_mode?.toUpperCase() === 'ETB' || p.payment_mode?.toUpperCase() !== 'USD')
                .reduce((sum, p) => sum + (parseFloat(String(p.amount).replace(/[^0-9.-]/g, '')) || 0), 0)
                .toFixed(2)} ETB
            </Typography>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 1.5 }} />
        
        {/* Payment Type Breakdown */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', display: 'block', mb: 1 }}>
          Payment Type Breakdown
        </Typography>
        <Grid container spacing={1} sx={{ pl: 1 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Shed Rental:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {filteredPayments.filter(p => p.payment_type?.toLowerCase() === 'shed rental').length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Land Leased:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {filteredPayments.filter(p => p.payment_type?.toLowerCase() === 'land leased').length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Residential:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {filteredPayments.filter(p => p.payment_type?.toLowerCase() === 'residential').length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Commercial:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {filteredPayments.filter(p => p.payment_type?.toLowerCase() === 'commercial').length}
            </Typography>
          </Grid>
        </Grid>
        
        {/* Note */}
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#CC0000', fontStyle: 'italic' }}>
          Note: USD and Birr amounts are shown separately. Exchange rate not applied.
        </Typography>
      </Paper>
    </Box>
  )}
</TabPanel>
      </Paper>
    </Box>
  );
};

export default CollectPayment;